import * as Sentry from "@sentry/browser";
import type { ViewerCameraOptions } from "@workspace/blocks";
import { useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "../../../i18n";
import { shouldCaptureException } from "../../../lib/sentry-filters";
import { HtmlViewer } from "../../HtmlViewer";
import { PLYViewer } from "../../PLYViewer";
import { SPZViewer } from "../../SPZViewer";
import { Alert, Button } from "../../ui";
interface Props extends ViewerCameraOptions {
	file: File;
	onError?: (error: Error) => void;
	heightClass?: string;
	previewMode?: boolean;
}
function PreviewFailure({ retry, error }: { retry: () => void; error?: Error }) {
	const { t } = useTranslation();
	return (
		<div className="flex h-full items-center justify-center p-4">
			<Alert tone="warning" title={t("upload.previewError")}>
				<p>{t("upload.previewErrorHint")}</p>
				{error?.message && (
					<details className="mt-3 text-xs">
						<summary className="cursor-pointer">{t("viewer.details")}</summary>
						<p className="mt-2 break-all">{error.message}</p>
					</details>
				)}
				<Button variant="secondary" className="mt-4" onClick={retry}>
					{t("upload.retryPreview")}
				</Button>
			</Alert>
		</div>
	);
}
export function FilePreview({ file, onError, heightClass = "h-full", previewMode = false, ...camera }: Props) {
	const { t } = useTranslation();
	const [url, setUrl] = useState<string | null>(null);
	const [error, setError] = useState<Error | null>(null);
	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		const objectUrl = URL.createObjectURL(file);
		setUrl(`${objectUrl}#model.${file.name.split(".").pop()?.toLowerCase()}`);
		setError(null);
		return () => URL.revokeObjectURL(objectUrl);
	}, [file]);
	const onPreviewError = useCallback(
		(error: Error) => {
			setError(error);
			onError?.(error);
			if (shouldCaptureException(error))
				Sentry.captureException(error, { extra: { fileName: file.name, fileSize: file.size, errorType: "preview" } });
		},
		[onError, file],
	);
	const retry = () => {
		setError(null);
		setAttempt(n => n + 1);
	};
	if (error) return <PreviewFailure retry={retry} error={error} />;
	if (!url)
		return (
			<output className="flex h-full items-center justify-center text-sm text-muted-foreground">
				{t("viewer.loading")}
			</output>
		);
	const extension = file.name.split(".").pop()?.toLowerCase();
	return (
		<ErrorBoundary
			key={attempt}
			fallbackRender={({ resetErrorBoundary, error }) => (
				<PreviewFailure
					error={error}
					retry={() => {
						retry();
						resetErrorBoundary();
					}}
				/>
			)}
		>
			<div className={`relative h-full w-full ${heightClass}`}>
				{extension === "html" ? (
					<HtmlViewer src={url} previewMode={previewMode} onError={onPreviewError} />
				) : extension === "spz" ? (
					<SPZViewer src={url} previewMode={previewMode} onError={onPreviewError} {...camera} />
				) : (
					<PLYViewer src={url} previewMode={previewMode} onError={onPreviewError} {...camera} />
				)}
			</div>
		</ErrorBoundary>
	);
}
